import { ArrowDownward, Delete, Edit, FileDownload } from '@mui/icons-material'
import { Box, Button, IconButton, LinearProgress, Link, Stack, Tooltip } from '@mui/material'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Accessibility, AlertDialog, RegistrationSearchForm } from '../../component/component'
import { deleteProduct, fetchRegistrationList, makeExternalCalls } from '../../service/Service'
import { AppContext } from '../../store/AppProvider'
import { SHOW_ERROR_ALERT, SHOW_INFO_ALERT, SHOW_SUCCESS_ALERT } from '../../store/reducer/AlertNotifierReducer'
import { ErrorConstant, Warnings } from '../../utils/Constants'
import { accessibility, FEATURES, isEmptyObj, scrollToElement } from '../../utils/Utils'
import './RegistrationSearch.css'

export const RegistrationSearch = () => {
    const navigate = useNavigate()
    const { regSearchParams, setToggleNav, setAlert, setShowLoader, activeRole } = useContext(AppContext)

    const [showTable, setShowTable] = useState(false)
    const [showLoading, setShowLoading] = useState(false)
    const [tableData, setTableData] = useState([])
    const [totalRecordCount, setTotalRecordCount] = useState(0)
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteRecord, setDeleteRecord] = useState({})

    //table state
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 20,
    });
    const [sorting, setSorting] = useState([]);

    const onSearch = useCallback(async (formData) => {
        try {
            setShowLoading(true)
            setTableData([]);
            const fetchedData = await fetchRegistrationList({ ...formData, ...pagination, sorting })
            if (fetchedData?.dataSizeError) {
                setAlert({ type: SHOW_INFO_ALERT, alertMessage: fetchedData?.dataSizeError });
                setTableData({})
                setShowLoading(false);
                return;
            }
            setTableData(fetchedData?.data)
            setTotalRecordCount(fetchedData?.totalCount)
            scrollToElement('search_table')
            setShowTable(true)
        } catch (error) {
            if (error?.code === 'ERR_NETWORK') {
                setAlert({ type: SHOW_INFO_ALERT, alertMessage: Warnings.optimizeSearch })
            } else {
                setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.error })
            }
        } finally {
            setShowLoading(false)
        }
    }, [pagination, sorting, setAlert])

    useEffect(() => {
        if (!isEmptyObj(regSearchParams)) {
            onSearch(regSearchParams)
        }
    }, [pagination, regSearchParams, onSearch])

    const handleDelete = (row) => {
        setShowDeleteModal(true);
        setDeleteRecord(row);
    }

    const initiateDeleteRequest = async (delete_reason) => {
        try {
            setShowDeleteModal(false)
            setShowLoader(true)
            const { registration_id } = deleteRecord;
            const { deletionStatus, token } = await deleteProduct({ registration_id: registration_id, delete_reason });
            if (deletionStatus === "SUCCESS") {
                const newTableData = [...tableData].filter(ele => ele?.registration_id !== registration_id)
                setTableData(newTableData)
                setShowDeleteModal(false);
                makeExternalCalls(token)
            }
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.error })
        } finally {
            setShowLoader(false)

        }
    }

    const navigateToDetailsPage = useCallback(({ registration_id }, enableEdit = false) => {
        setToggleNav(false);
        navigate(`${registration_id}/details`, { state: { registrationId: registration_id, enableEdit } })
    }, [navigate, setToggleNav])

    const columns = useMemo(
        () => [
            {
                accessorKey: 'registration_id',
                header: 'Registration Id',
                size: 150,
                Cell: ({ row: { original } }) => {
                    return accessibility(FEATURES.VIEW_REGISTRATIONS_DETAILS, activeRole)
                        ? <Link component="button" onClick={() => navigateToDetailsPage(original)}>
                            {original?.registration_id}
                        </Link>
                        : original?.registration_id
                }
            },
            {
                accessorKey: 'user_name',
                header: 'User Name',
                size: 130,
                accessorFn: (row) => `${row.first_name} ${row.last_name}`
            },
            {
                accessorKey: 'email',
                header: 'Email',
                size: 150,
            },
            {
                accessorKey: 'registration_serial_number',
                header: 'Registration Serial Number',
                size: 150,
            },
            {
                accessorKey: 'sku_name',
                header: 'Sku Name',
                size: 150,
            },
            {
                accessorKey: 'registration_date',
                header: 'Registration Date',
                size: 150,
            },
            {
                accessorKey: 'purchase_date',
                header: 'Purchase Date',
                size: 150,
            },
            {
                accessorKey: 'date_of_delivery',
                header: 'Date of Delivery',
                size: 150,
            },
            {
                accessorKey: 'registration_active',
                header: 'Active',
                size: 150,
                accessorFn: (row) => String(row.registration_active)
            },

            {
                accessorKey: 'retailer_name',
                header: 'Retailer Name',
                size: 150,
            },
            {
                accessorKey: 'registration_warranty_end_date',
                header: 'Registration Warranty End Date',
                size: 210,
            },
            {
                accessorKey: 'registration_manufacture_warranty_end_date',
                header: 'Manufacture Warranty End Date',
                size: 250,
            },
            {
                accessorKey: 'campaign_id',
                header: 'Campaign Id',
                size: 150,
            },
            {
                accessorKey: 'source',
                header: 'Source',
                size: 150,
            },
            {
                accessorKey: 'locale',
                header: 'Locale',
                size: 150,
            },
            {
                accessorKey: 'receive_communication',
                header: 'OptIn',
                size: 150,
                accessorFn: (row) => String(row.receive_communication)
            },
            {
                enableSorting: false,
                accessorKey: 'action', //access nested data with dot notation
                header: 'Action',
                size: 50,
                muiTableHeadCellProps: {
                    align: 'center',
                },
                Cell: ({ row }) => (
                    <Stack direction={"row"} spacing={1}>
                        <Accessibility feature={FEATURES.EDIT_REGISTRATIONS}>
                            <IconButton
                                color="primary"
                                onClick={() => navigateToDetailsPage(row?.original, true)}
                            >
                                <Tooltip title="Edit">
                                    <Edit />
                                </Tooltip>
                            </IconButton>
                        </Accessibility>
                        <Accessibility feature={FEATURES.DELETE_REGISTRATION}>
                            <IconButton
                                color="error"
                                onClick={() => handleDelete(row?.original)}
                            >
                                <Tooltip title="Delete">
                                    <Delete />
                                </Tooltip>
                            </IconButton>
                        </Accessibility>
                    </Stack>
                ),
            },
        ],
        [navigateToDetailsPage, activeRole],
    );

    const handleExportRegData = async (columns) => {
        try {
            setAlert({ type: SHOW_INFO_ALERT, alertMessage: "CSV Download started..." })
            const fetchedData = await fetchRegistrationList({ ...regSearchParams, ...pagination, isDownload: true });
            window.open(fetchedData, "_blank");
            setAlert({ type: SHOW_SUCCESS_ALERT, alertMessage: "CSV Download successful!" })
        } catch (e) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.downloadingCSV })
        }
    }

    const showActionColumn = accessibility(FEATURES.EDIT_REGISTRATIONS, activeRole) && accessibility(FEATURES.DELETE_REGISTRATION, activeRole)

    const table = useMaterialReactTable({
        columns,
        data: tableData,
        enableStickyHeader: true,
        positionGlobalFilter: 'right',
        // manualFiltering: false,
        enableGlobalFilter: false,
        // onGlobalFilterChange: setGlobalFilter,
        showHideFilters: false,
        // enableTopToolbar: true,
        enableColumnFilter: false,
        enableHiding: true, //this shows the hide/ show columns
        // enableFullScreenToggle: true,
        enableColumnActions: false,
        enableDensityToggle: false,
        //  enableFilters: false, //this shows the search icon and filter icon together
        enableColumnFilters: false, //this hides the column filter
        muiSearchTextFieldProps: {
            placeholder: `Search by hardware SKU`,
            sx: { minWidth: '300px' },
            variant: 'outlined',
        },
        muiTableContainerProps: { sx: { maxHeight: '400px' } },
        initialState: {
            columnVisibility: { action: showActionColumn },
            columnPinning: { left: ['registration_id'], right: ['action'] },
            density: 'comfortable',
        },
        muiTableHeadCellProps: {
            align: 'center',
            //simple styling with the `sx` prop, works just like a style prop in this example
            sx: {
                backgroundColor: '#1976d2',
                color: 'white',
                fontSize: '14px',
                verticalAlign: 'middle',
            },
        },
        muiColumnActionsButtonProps: ({ column, table }) => (<ArrowDownward />),
        muiTableBodyCellProps: {
            align: 'center',
            sx: {
                borderRight: '1px solid #e0e0e0',
                verticalAlign: 'middle',
            }
        },
        renderTopToolbarCustomActions: ({ table }) => (
            <Box
                sx={{
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
                    onClick={() => handleExportRegData(table?.getVisibleFlatColumns())}
                    startIcon={<FileDownload />}
                >
                    Export CSV
                </Button>
            </Box>
        ),
        manualPagination: true,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        paginationDisplayMode: 'pages',
        rowCount: totalRecordCount, //should be the total records coming from response
        muiPaginationProps: {
            showRowsPerPage: false,
            shape: 'rounded',
            showFirstButton: true,
            showLastButton: true,
        },
        state: {
            pagination,
            sorting,
        },

    });

    return (
        <div>
            <div className='header-title' style={{ letterSpacing: '0.5px' }}>{('Registration Search').toUpperCase()}</div>
            <RegistrationSearchForm onSearch={(formData) => onSearch(formData)} loading={showLoading} />
            <div id={"search_table"} style={{ marginTop: 25 }}>
                {showLoading && <LinearProgress />}
                {showTable &&
                    <MaterialReactTable
                        table={table}
                    />}
            </div>
            <AlertDialog
                title={`Delete Confirmation`}
                description={
                    <>
                        Are you sure you want to delete this record?
                    </>
                }
                primaryButton="Submit"
                show={showDeleteModal}
                handleConfirmation={(reason) => initiateDeleteRequest(reason)}
                handleCancel={() => setShowDeleteModal(false)} />
        </div>
    )
}
